import { useState, type ComponentProps } from 'react';
import dynamic from 'next/dynamic';
import { Collapse } from 'antd';
import { useRouter } from 'next/router';

import { ProgressiveImg } from 'components/base';
import { useI18n } from 'context/i18n';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { isBlank } from 'utils/StringUtils';
import type { TSeoContent } from 'types/product-list';
import { getDomainValue, isDomain } from 'utils/domain';
import { Disclosure, DisclosureGroup, DisclosureHeader, DisclosurePanel } from './ui/disclosure';
import { Link } from './ui/link';
import { Button } from './ui/button';
import { Heading } from 'react-aria-components';

const CmsGridBlock = dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.Grid), { ssr: false });

type SeoContentProps = {
  camperlab: boolean;
  content: TSeoContent;
};

function SeoHeading({ children }: React.PropsWithChildren<{}>) {
  return (
    <Heading
      level={2}
      className="px-10 py-8 text-xl font-medium nnormal:text-center nnormal:font-secondary nnormal:text-inverted lab:px-0 lab:py-0 lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs"
    >
      {children}
    </Heading>
  );
}

export function SeoContent({ content, camperlab }: SeoContentProps) {
  const { t } = useI18n();
  const router = useRouter();
  const [showBlockTwo, setShowBlockTwo] = useState(() => {
    if (isDomain('camperlab') && !isBlank(content?.blockOne?.body) && !isBlank(content?.blockTwo?.body)) {
      return false;
    }
    return true;
  });

  const thirdRowBlank = [isBlank(content?.blockThree?.body), isBlank(content?.blockFour?.body), isBlank(content?.blockFive?.body)].every(
    (item) => item === true,
  );

  return (
    <div className="mt-8 flex w-full flex-col [--bg:#efeee8] nnormal:bg-[--bg] nnormal:text-inverted nnormal:[--bg:black] lab:[--bg:transparent] [&_h2]:mb-0 [&_h3]:mb-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_p]:mb-0 [&_picture]:h-full [&_picture]:w-full">
      {content?.cms ?
        <>
          <SeoHeading>{t('grid.seo', 'related.products')}</SeoHeading>
          <CmsGridBlock {...content.cms} slug={router.asPath.split('?')?.[0] ?? ''} />
        </>
      : null}
      {!isBlank(content?.blockOne?.body) ?
        <div className="grid grid-cols-2 grid-rows-[1fr_300px] sm:grid-rows-[1fr_minmax(300px,500px)] md:grid-cols-3 md:grid-rows-[540px] lg:grid-cols-4 lg:grid-rows-1">
          <a href={content.images?.[0]?.href ?? undefined} className="flex overflow-hidden">
            <ProgressiveImg src={content.images?.[0]?.src} alt={content.images?.[0]?.alt ?? ''} />
          </a>
          <a href={content.images?.[1]?.href ?? undefined} className="flex overflow-hidden md:hidden lg:flex">
            <ProgressiveImg src={content.images?.[1]?.src} alt={content.images?.[1]?.alt ?? ''} />
          </a>
          <div
            className="col-start-1 col-end-3 row-start-1 flex w-full flex-col overflow-hidden bg-[--bg] p-6 lab:justify-center md:col-start-2 md:col-end-4 lg:col-start-3 lg:col-end-5 [&_h2]:text-2xl nnormal:[&_h2]:mb-10 nnormal:[&_h2]:font-secondary nnormal:[&_h2]:text-inverted lab:[&_h2]:mb-10 lab:[&_h2]:text-sm lab:[&_h2]:uppercase lab:lg:[&_h2]:text-xs lab:[&_p]:text-sm lab:[&_p]:font-normal camper:md:[&_p]:mt-12 camper:md:[&_p]:px-12 camper:md:[&_p]:py-0 camper:md:[&_p]:pl-8 lab:lg:[&_p]:text-xs"
            dangerouslySetInnerHTML={{ __html: content.blockOne?.body }}
          ></div>
        </div>
      : null}
      {!isBlank(content?.blockTwo?.body) ?
        showBlockTwo ?
          <div className="grid grid-cols-2 grid-rows-[1fr_300px] sm:grid-rows-[1fr_minmax(300px,500px)] md:grid-cols-3 md:grid-rows-[540px] lg:grid-cols-4 lg:grid-rows-1">
            <a href={content.images?.[2]?.href ?? undefined} className="flex">
              <ProgressiveImg src={content.images?.[2]?.src} alt={content.images?.[2]?.alt ?? ''} />
            </a>
            <a href={content.images?.[3]?.href ?? undefined} className="flex overflow-hidden md:hidden lg:flex">
              <ProgressiveImg src={content.images?.[3]?.src} alt={content.images?.[3]?.alt ?? ''} />
            </a>
            <div
              className="col-start-1 col-end-3 row-start-1 flex w-full flex-col overflow-hidden bg-[--bg] p-6 lab:justify-center [&_h2]:text-2xl nnormal:[&_h2]:mb-10 nnormal:[&_h2]:font-secondary nnormal:[&_h2]:text-inverted lab:[&_h2]:mb-10 lab:[&_h2]:text-sm lab:[&_h2]:uppercase lab:lg:[&_h2]:text-xs lab:[&_p]:text-sm lab:[&_p]:font-normal camper:md:[&_p]:mt-12 camper:md:[&_p]:px-12 camper:md:[&_p]:py-0 camper:md:[&_p]:pl-8 lab:lg:[&_p]:text-xs"
              dangerouslySetInnerHTML={{ __html: content.blockTwo?.body }}
            ></div>
          </div>
        : <div className="flex items-center justify-center px-4 py-5 lg:py-10">
            <Button variant="outline" onPress={() => setShowBlockTwo(true)}>
              {t('listado.productos', 'ver.mas')}
            </Button>
          </div>

      : null}
      {!thirdRowBlank && isDomain('camper') ?
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <div
            className="overflow-hidden bg-[var(--color-brand)] p-6 text-[var(--color-white)] [&_*]:!text-[var(--color-white)] [&_h3]:text-2xl [&_p]:mt-4"
            dangerouslySetInnerHTML={{ __html: content.blockThree?.body }}
          />
          <div
            className="overflow-hidden bg-[var(--color-black)] p-6 text-[var(--color-white)] selection:bg-[var(--color-white)] selection:text-[var(--color-black)] [&_*]:!text-[var(--color-white)] [&_h3]:text-2xl [&_p]:mt-4"
            dangerouslySetInnerHTML={{ __html: content.blockFour?.body }}
          />
          <div className="flex items-center justify-center bg-[#e5dcd3] md:hidden lg:flex">
            <img className="!object-contain" src="/assets-new/svg/grid-seo-recicle.svg" alt="" />
          </div>
          <div className="overflow-hidden p-6 [&_h3]:text-2xl [&_p]:mt-4" dangerouslySetInnerHTML={{ __html: content.blockFive?.body }} />
        </div>
      : null}
      {Array.isArray(content?.faqs) && content.faqs.length > 0 ?
        <div className="mx-auto mt-16 w-full max-w-[896px] px-4 lab:mt-10">
          <SeoHeading>{content?.faqs_title || t('grid.seo', 'faq.title')}</SeoHeading>

          <DisclosureGroup allowsMultipleExpanded className={`mt-8 lab:mt-10`}>
            {content.faqs.map((faq, index) => (
              <Disclosure key={`faq-${index}`}>
                <DisclosureHeader buttonClassName="font-medium text-base lg:text-base lab:text-sm lab:lg:text-xs lab:font-bold nnormal:text-inverted" level={3}>
                  {faq.question}
                </DisclosureHeader>
                <DisclosurePanel>
                  <p className="text-sm" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </DisclosurePanel>
              </Disclosure>
            ))}
          </DisclosureGroup>
          <script
            id="ldFaq"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: content?.faqs?.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                  },
                })),
              }),
            }}
          />
        </div>
      : null}
      {!camperlab && Array.isArray(content?.links) && content.links.length > 0 ?
        <div className="mx-auto my-16 w-full max-w-[896px] px-4">
          <SeoHeading>{t('grid.seo', 'other.categories')}</SeoHeading>
          <div className="mt-8 flex flex-wrap gap-4 lab:mt-10 lab:gap-2.5">
            {content.links.map((link, index) => (
              <Link
                variant="outline"
                href={link.url}
                color={getDomainValue({
                  camper: 'primary',
                  nnormal: 'inverted',
                  camperlab: 'primary',
                })}
                key={`link-${index}`}
              >
                {link.title}
              </Link>
            ))}
          </div>
        </div>
      : null}
    </div>
  );
}
